import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { MUTATION_KEY, QUERY_KEY } from './keys';
import { queryClient } from './queryClient';
import { clmApiClient } from '../clients';
import type {
  ApiErrorResponse,
  DepartmentCreateRequest,
  DepartmentCreateRequestBody,
  DepartmentDetails,
} from '../types';
import { isApiErrorResponse } from '../utils';

const updateDepartment = async (
  data: DepartmentCreateRequest,
): Promise<DepartmentDetails> => {
  const response = await clmApiClient.put<DepartmentDetails, ApiErrorResponse>(
    `department/${data.id}`,
    {
      department: data,
    } as DepartmentCreateRequestBody,
  );

  if (!response.data) {
    throw new Error('No data returned from update department');
  }

  if (!response.ok) {
    if (isApiErrorResponse(response.data)) {
      throw new Error(response.data.details);
    }
    throw new Error('Unknown error');
  }

  await queryClient.invalidateQueries({
    predicate: (query) => {
      const [key, departmentId] = query.queryKey as [string, string];

      if (
        key === QUERY_KEY.DEPARTMENT_SEARCH ||
        key === QUERY_KEY.DEPARTMENTS_ALL
      ) {
        return true;
      }
      if (key === QUERY_KEY.DEPARTMENT_DETAILS && departmentId === data.id) {
        return true;
      }

      return false;
    },
  });

  return response.data;
};

export const useDepartmentUpdateMutation = (
  config?: Omit<
    UseMutationOptions<DepartmentDetails, Error, DepartmentCreateRequest>,
    'mutationFn'
  >,
) =>
  useMutation({
    mutationKey: [MUTATION_KEY.DEPARTMENT_UPDATE],
    mutationFn: updateDepartment,
    ...config,
  });
